import React, { useState, useEffect, useRef } from 'react';
import { callPhone, getMyPhone, setMyPhone, textDriver, aiCall, noteCallIntent } from '../../truckmateStore';
import { phone as browserPhone, dialOnPhone } from './PhonePanel';

// Click-to-call through RingCentral. With the browser phone signed in (Phone, top of the console) it dials there;
// otherwise RingOut: the dispatcher's own phone rings first, and when they answer it dials this number from the
// company line (the first time, it asks which phone should ring, saved per dispatcher). Either way the backend is
// told which load the call is for, so the recording and transcript land on it.

let myPhoneCache;   // shared by every Call button on the page
// which load the call is for (best effort: the call goes ahead either way)
const tellLoad = (to, trip) => { try { const p = noteCallIntent(to, trip); if (p && p.catch) p.catch(() => {}); } catch { /* not available */ } };

// A popup closes with its ✕, a tap anywhere outside it, or Escape. A half-typed text stays for next time.
function useDismiss(open, close) {
  const ref = useRef(null);
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (ref.current && !ref.current.contains(e.target)) closeRef.current(); };
    const key = (e) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', key); };
  }, [open]);
  return ref;
}
const CloseX = ({ onClose }) => <button type="button" className="tmw-ask-x" aria-label="Close this box" onClick={onClose}>✕</button>;

export default function CallButton({ phone, trip, label }) {
  const [state, setState] = useState('');      // '' | 'ask' | 'calling' | 'ringing' | 'error'
  const [mine, setMine] = useState('');
  const [msg, setMsg] = useState('');
  const box = useDismiss(state === 'ask', () => setState(''));
  if (!phone) return null;

  const place = async () => {
    setState('calling'); setMsg('');
    try {
      tellLoad(phone, trip);
      const r = await callPhone(phone, { trip, label });
      setState('ringing'); setMsg(`Your phone ${r.ringing || ''} is ringing — answer to connect.`);
      setTimeout(() => setState(''), 12000);
    } catch (e) {
      if (e.needPhone) { setState('ask'); return; }
      setState('error'); setMsg(e.message || 'Could not place the call.');
    }
  };
  const start = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (state === 'ask') { setState(''); return; }
    if (browserPhone.loggedIn) {
      if (!window.confirm(`Call ${phone}${label ? ` (${label})` : ''} from the browser phone?`)) return;
      tellLoad(phone, trip);
      if (dialOnPhone(phone)) { setState('ringing'); setMsg('Calling on the browser phone.'); setTimeout(() => setState(''), 8000); return; }
    }
    if (myPhoneCache === undefined) { try { myPhoneCache = (await getMyPhone()).phone || null; } catch { myPhoneCache = null; } }
    if (!myPhoneCache) { setState('ask'); return; }
    if (!window.confirm(`Call ${phone}${label ? ` (${label})` : ''}?\nYour phone ${myPhoneCache} rings first.`)) return;
    place();
  };
  const saveMine = async (e) => {
    e.preventDefault();
    try { const r = await setMyPhone(mine); myPhoneCache = r.phone; setState(''); place(); }
    catch (ex) { setMsg(ex.message || 'Could not save.'); }
  };

  return (
    <span className="tmw-call" ref={box}>
      <button type="button" className="tmw-call-btn" onClick={start} disabled={state === 'calling'} aria-label={`Call ${label || phone}`} title="Call from the company number (your phone rings first)">
        {state === 'calling' ? 'Calling…' : '📞 Call'}
      </button>
      {state === 'ask' && (
        <form className="tmw-call-ask" onSubmit={saveMine} onClick={(e) => e.stopPropagation()}>
          <CloseX onClose={() => setState('')} />
          <label>Which phone should ring first?<input value={mine} onChange={(e) => setMine(e.target.value)} inputMode="tel" placeholder="your desk, RingCentral app or cell" autoFocus /></label>
          <button type="submit" className="tmw-btn tmw-btn-sm tmw-btn-primary">Save &amp; call</button>
          <button type="button" className="tmw-btn tmw-btn-sm" onClick={() => setState('')}>Cancel</button>
          {msg && <div className="tmw-small tmw-call-err">{msg}</div>}
        </form>
      )}
      {(state === 'ringing' || state === 'error') && msg && <span className={`tmw-small${state === 'error' ? ' tmw-call-err' : ''}`}>{msg}</span>}
    </span>
  );
}

export function resetMyPhoneCache(v) { myPhoneCache = v; }

// 💬 Text: send the driver a message in your own words. The company name goes
// first and "Reply STOP to opt out." last (as registered with the carriers).
// Only sends to drivers whose consent is recorded — the server checks.
export function TextButton({ trip, which = 1, name, phone, blocked = null }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [state, setState] = useState('');
  const [msg, setMsg] = useState('');
  const box = useDismiss(open, () => setOpen(false));
  if (!phone || !trip) return null;
  const body = text.replace(/\s+/g, ' ').trim();
  const preview = body ? `Florida Beauty Flora dispatch: ${body}${/reply stop/i.test(body) ? '' : ' Reply STOP to opt out.'}` : '';
  const send = async (e) => {
    e.preventDefault();
    if (!body) return;
    setState('sending'); setMsg('');
    try {
      await textDriver(trip, { kind: 'custom', text: body, driver: which });
      setState('sent'); setMsg(`Sent to ${name || phone}.`); setText('');
      setTimeout(() => { setOpen(false); setState(''); setMsg(''); }, 2500);
    } catch (ex) { setState('error'); setMsg(ex.message || 'Could not send.'); }
  };
  return (
    <span className="tmw-call" ref={box}>
      <button type="button" className="tmw-call-btn" aria-label={`Text ${name || phone}`} disabled={!!blocked} title={blocked || undefined} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}>💬 Text</button>
      {open && (
        <form className="tmw-call-ask tmw-text-box" onSubmit={send} onClick={(e) => e.stopPropagation()}>
          <CloseX onClose={() => setOpen(false)} />
          <label>Message to {name || phone}<textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={280} rows={3} autoFocus placeholder="e.g. Call me when you're empty at the receiver" /></label>
          {preview && <div className="tmw-small">They’ll get: “{preview}”</div>}
          <div className="tmw-actions" style={{ marginTop: 0 }}>
            <button type="submit" className="tmw-btn tmw-btn-sm tmw-btn-primary" disabled={!body || state === 'sending'}>{state === 'sending' ? 'Sending…' : 'Send text'}</button>
            <button type="button" className="tmw-btn tmw-btn-sm" onClick={() => setOpen(false)}>Cancel</button>
          </div>
          {msg && <div className={`tmw-small${state === 'error' ? ' tmw-call-err' : ''}`}>{msg}</div>}
        </form>
      )}
    </span>
  );
}

// 🤖 AI call: Jarvis (Retell) calls the driver in English or Spanish, then the
// summary lands on the load. A dispatcher's click only; consent + no STOP required.
const AI_PURPOSES = [['check', 'Check call — where are you, ETA, any problem'], ['confirm-stop', 'Confirm the next stop was delivered'], ['pod', 'Ask for the signed POD / BOL']];
export function AiCallButton({ trip, which = 1, name, phone }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('');
  const [msg, setMsg] = useState('');
  const box = useDismiss(open, () => setOpen(false));
  if (!phone || !trip) return null;
  const go = async (purpose) => {
    setState('calling'); setMsg('');
    try { await aiCall(trip, { driver: which, purpose }); setState('ok'); setMsg(`Jarvis is calling ${name || phone} — the summary will show on this load.`); setTimeout(() => { setOpen(false); setState(''); setMsg(''); }, 6000); }
    catch (ex) { setState('error'); setMsg(ex.message || 'Could not start the call.'); }
  };
  return (
    <span className="tmw-call" ref={box}>
      <button type="button" className="tmw-call-btn" aria-label={`AI call ${name || phone}`} title="Jarvis (AI) calls the driver in English or Spanish" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}>🤖 AI call</button>
      {open && (
        <span className="tmw-call-ask" onClick={(e) => e.stopPropagation()}>
          <CloseX onClose={() => setOpen(false)} />
          <span className="tmw-small"><b>Jarvis calls {name || 'the driver'} about:</b></span>
          {AI_PURPOSES.map(([k, label]) => <button key={k} type="button" className="tmw-btn tmw-btn-sm" disabled={state === 'calling'} onClick={() => go(k)}>{label}</button>)}
          <button type="button" className="tmw-btn tmw-btn-sm" onClick={() => setOpen(false)}>Cancel</button>
          {msg && <span className={`tmw-small${state === 'error' ? ' tmw-call-err' : ''}`}>{msg}</span>}
        </span>
      )}
    </span>
  );
}
